import dayjs from 'dayjs';

import { IStatusHistoryItem, OrdersStatus } from '@/models/orders.model';

export type StatusStepState = 'completed' | 'current' | 'future' | 'skipped' | 'cancelled';

const MAIN_FLOW: OrdersStatus[] = [
  OrdersStatus.NEW,
  OrdersStatus.PROCESSING,
  OrdersStatus.ACCEPTED,
  OrdersStatus.READY_FOR_TTN,
  OrdersStatus.TRANSFERRED,
  OrdersStatus.AWAITING,
  OrdersStatus.COMPLETED,
];

const hasValidDate = (date: string | null | undefined): boolean => {
  return !!date && dayjs(date).isValid();
};

const getFurthestMainIndex = (history: IStatusHistoryItem[], currentStatus?: OrdersStatus): number => {
  let furthest = -1;

  MAIN_FLOW.forEach((status, index) => {
    const item = history.find((entry) => entry.status === status);
    if (item && hasValidDate(item.date)) {
      furthest = index;
    }
  });

  if (currentStatus && currentStatus !== OrdersStatus.RESERVE && currentStatus !== OrdersStatus.CANCELLED) {
    const statusIndex = MAIN_FLOW.indexOf(currentStatus);
    if (statusIndex > furthest) {
      furthest = statusIndex;
    }
  }

  return furthest;
};

export const getStatusStepState = (
  item: IStatusHistoryItem,
  history: IStatusHistoryItem[],
  currentStatus?: OrdersStatus,
): StatusStepState => {
  const isOrderCancelled =
    currentStatus === OrdersStatus.CANCELLED ||
    history.some((entry) => entry.status === OrdersStatus.CANCELLED && hasValidDate(entry.date));

  if (item.status === OrdersStatus.CANCELLED && (hasValidDate(item.date) || currentStatus === OrdersStatus.CANCELLED)) {
    return 'cancelled';
  }

  const furthestMainIndex = getFurthestMainIndex(history, currentStatus);
  const reserveItem = history.find((entry) => entry.status === OrdersStatus.RESERVE);
  const reserveHasDate = !!reserveItem && hasValidDate(reserveItem.date);
  const isInReserve = currentStatus === OrdersStatus.RESERVE || reserveHasDate;

  if (item.status === OrdersStatus.RESERVE) {
    if (!hasValidDate(item.date) && currentStatus !== OrdersStatus.RESERVE) {
      return furthestMainIndex >= 1 || isOrderCancelled ? 'skipped' : 'future';
    }

    if (isOrderCancelled || furthestMainIndex >= 1) {
      return 'completed';
    }

    return 'current';
  }

  const mainIndex = MAIN_FLOW.indexOf(item.status);
  if (mainIndex === -1) {
    return 'future';
  }

  if (!hasValidDate(item.date) && item.status !== currentStatus) {
    if (furthestMainIndex > mainIndex || isOrderCancelled) {
      return 'completed';
    }
    return 'future';
  }

  if (item.status === OrdersStatus.COMPLETED || isOrderCancelled) {
    return 'completed';
  }

  if (mainIndex < furthestMainIndex) {
    return 'completed';
  }

  if (isInReserve && furthestMainIndex <= 0 && item.status === OrdersStatus.NEW) {
    return 'completed';
  }

  if (mainIndex === furthestMainIndex || item.status === currentStatus) {
    return 'current';
  }

  return 'current';
};
